import React, { useState } from 'react';
import {
  Page,
  CompactTable,
  useFetch,
  useFilters,
  usePageQuery,
  PermissionGuard,
  useUserinfo,
  RemoteRequest,
  openNotificationWithIcon,
} from '@mss/mss-component-library';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import columns from './columns';
import PageSidebar from 'jsx/common/FilterSidebar';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import RequestAdminModal from '../../../components/RequestAdminModal';
import { TFunction } from 'i18next';

const filterKeys: any[] = [
  'asset_name',
  'user_name',
  'profile_name',
  'status',
  'handled_by_user_name',
];
const searchKeys: any[] = [
  'asset_name',
  'user_name',
  'profile_name',
  'status',
  'status_text',
  'handled_by_user_name',
];

const customOptionLabel = (t: TFunction) => (key: string, value: any) => {
  if (key === 'status') {
    switch (value) {
      case 'awaiting_approval':
        return t('Awaiting approval');
      case 'wait_for_start_date':
        return t('Future approved');
      case 'allow':
        return t('Approved');
      case 'denied':
        return t('Denied');
      case 'revoked':
        return t('Revoked');
      case 'expired':
        return t('Expired');
      case 'expired_unapproved':
        return t('Expired approval');
      default:
        return value;
    }
  }
  return value;
};

const customHeadingLabel = (t: TFunction) => (key: string) => {
  if (key === 'asset_name') return t('Target Asset');
  if (key === 'profile_name') return t('Profile');
  if (key === 'status') return t('Status');
  if (key === 'user_name') return t('User');
  if (key === 'handled_by_user_name') return t('Handled by');

  return key;
};

const PAGE_SIZE = 10;
type EnrichedRequest = RemoteRequest & {
  asset_name: string;
  user_name: string;
  profile_name: string;
  handled_by_user_name: string;
  profile_object: any;
  approval_user_object: any;
};
export default function List() {
  const [hasPermission] = useUserinfo();
  const [sessionRequests, setSessionsRequests] = useState<EnrichedRequest[]>(
    []
  );
  const [modal, setModal] = useState<RemoteRequest | null>(null);
  const [t] = useTranslation(['remoteAccess', 'common', 'asset', 'profile']);

  const [rawRequests, getRequest] = useFetch({
    url: '/api/remote-request/',
    defaultValue: [],
    initialFetch: hasPermission('admin_view_remoterequest'),
    onSucces: async ({ data }: any) => {
      try {
        setSessionsRequests(
          data.map((x: EnrichedRequest) => ({
            ...x,
            asset_name: x.asset_object.name,
            user_name: x.request_user_object.name,
            profile_name: x.profile_object.name,
            status_text: x.approval_status === 'allow' ? 'approved' : null,
            handled_by_user_name: x.approval_user_object?.name || '',
          }))
        );
      } catch {
        openNotificationWithIcon(
          notification.error,
          t('Failed fetch assets'),
          t(
            'Due to technical issues not all information could not be retrieved. Therefore you will only see the ids of the assets and users. Please try again later.'
          )
        );
      }
    },
  });

  const isFetching = rawRequests.loading;

  const [availableFilters, toggleFilter, filteredData, search] = useFilters(
    sessionRequests,
    filterKeys,
    searchKeys,
    customOptionLabel(t),
    customHeadingLabel(t)
  );
  const [page, goToPage] = usePageQuery(filteredData.length / PAGE_SIZE);

  return (
    <PermissionGuard codename={'admin_view_remoterequest'}>
      <Page
        breadCrumbs={<Breadcrumbs />}
        padding={false}
        main={
          <PermissionGuard codename={'admin_view_remoterequest'}>
            <CompactTable
              bordered={false}
              dataSource={filteredData}
              loading={isFetching}
              columns={columns(t)}
              rowKey={'id'}
              onRow={(record: any) => ({
                onClick: () => setModal(record),
                style: { cursor: 'default' },
              })}
              pagination={{
                current: page,
                showQuickJumper: true,
                showSizeChanger: true,
                total: filteredData.length,
                showTotal: (total: number, range: any[]) =>
                  t('(RANGE1)-(RANGE2) of (TOTAL) items', {
                    range1: range[0],
                    range2: range[1],
                    TOTAL: total,
                  }),
              }}
              onChange={(pagination: any) => {
                if (page !== pagination.current) goToPage(pagination.current);
              }}
            />
            {!!modal && (
              <RequestAdminModal
                request={modal}
                closeModal={() => {
                  setModal(null);
                  getRequest();
                }}
              />
            )}
          </PermissionGuard>
        }
        sidebar={
          <PermissionGuard codename={'admin_view_remoterequest'}>
            <PageSidebar
              availableFilters={availableFilters}
              toggleFilter={toggleFilter}
              search={search}
            />
          </PermissionGuard>
        }
      />
    </PermissionGuard>
  );
}
